import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import { message, Skeleton } from "antd";
import {
  ChevronDown,
  ChevronRight,
  Database,
  FileText,
  Globe,
  Server,
  Radio,
  Users as UsersIcon,
} from "lucide-react";
import { AUTH_TOKEN, DOMAIN_ID, getUserName, ORG_ID, isEmpty, manageServiceError } from "../../utils/helpers";
import { getListConnections } from "../../service/pamService";
import { didAxios } from "../../service/didService";
import { appAxios } from "../../service/appService";
import { AppContentLayout } from "../../layout/applayout";
import { SimpleSelect } from "../../components/ui/UserInteraction";
import { useLocation } from "react-router";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../design-system/tokens";

const TONES = {
  doc: { bg: "var(--an-primaryL)", fg: AN_COLORS.primary },
  group: { bg: "var(--an-infoBg)", fg: "var(--an-infoFg)" },
  svc: { bg: "#EEF3FA", fg: "#3C5278" },
  ep: { bg: "#FEF2D8", fg: "#A67017" },
  rad: { bg: "#FFE7D8", fg: "#C25520" },
  db: { bg: "#E6F5EC", fg: "#2A8F5B" },
};

function StatusDot({ status }) {
  const color =
    status === "Audit" ? AN_COLORS.danger : status === "Live" ? AN_COLORS.success : AN_COLORS.warn;
  return (
    <span
      style={{
        width: 9,
        height: 9,
        borderRadius: "50%",
        background: color,
        display: "inline-block",
      }}
    />
  );
}

function EdStat({ label, value, loading }) {
  return (
    <div>
      <div
        style={{
          fontFamily: AN_MONO,
          fontSize: 10.5,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: AN_COLORS.muted,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 28,
          fontWeight: 600,
          letterSpacing: -0.6,
          color: AN_COLORS.ink,
          display: "flex",
          alignItems: "center",
          gap: 8,
          minHeight: 34,
        }}
      >
        {loading ? <Skeleton.Input active size="small" style={{ width: 60, height: 28 }} /> : value}
      </div>
    </div>
  );
}

function EditorialRow({ tone, icon, title, count, unit, loading, onClick, first }) {
  const t = TONES[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "grid",
        gridTemplateColumns: "44px 1fr 120px 90px",
        gap: 18,
        alignItems: "center",
        padding: "18px 22px",
        textAlign: "left",
        background: "transparent",
        border: "none",
        borderTop: first ? "none" : `1px solid ${AN_COLORS.line2}`,
        cursor: "pointer",
        width: "100%",
        fontFamily: AN_FONT,
        color: AN_COLORS.ink,
      }}
    >
      <span
        style={{
          width: 34,
          height: 34,
          borderRadius: 8,
          background: t.bg,
          color: t.fg,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </span>
      <div>
        <div style={{ fontSize: 14.5, fontWeight: 600, letterSpacing: -0.1 }}>{title}</div>
        <div style={{ fontSize: 12.5, color: AN_COLORS.muted, marginTop: 2 }}>
          {loading ? (
            <Skeleton.Input active size="small" style={{ width: 220, height: 14 }} />
          ) : (
            <>You have {count ?? 0} {unit} across all your policies.</>
          )}
        </div>
      </div>
      <div
        style={{
          fontFamily: AN_MONO,
          fontSize: 22,
          fontWeight: 500,
          letterSpacing: -0.5,
          color: AN_COLORS.ink,
          textAlign: "right",
        }}
      >
        {loading ? "—" : count ?? 0}
      </div>
      <div
        style={{
          textAlign: "right",
          color: AN_COLORS.primary,
          fontSize: 12.5,
          fontWeight: 500,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 4,
        }}
      >
        View <ChevronRight size={11} />
      </div>
    </button>
  );
}

function UserDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [messageApi, contextHolder] = message.useMessage();
  const [initialDayVal, setInitialDayVal] = useState("month");
  const [connectionCount, setConnectionCount] = useState(null);
  const [endpointCount, setEndpointCount] = useState(null);
  const [adPolicyCount, setADPolicyCount] = useState(null);
  const [adGroupPolicyCount, setADGroupPolicyCount] = useState(null);
  const [servicePolicyCount, setServicePolicyCount] = useState(null);
  const [endpointsPolicyCount, setEndpointsPolicyCount] = useState(null);
  const [radiusPolicyCount, setRadiusPolicyCount] = useState(null);
  const [databasePolicyCount, setDatabasePolicyCount] = useState(null);
  const [tenantData, setTenantData] = useState(null);
  const [isConnectionLoading, setIsConnectionLoading] = useState(false);
  const [isEndpointLoading, setIsEndpointLoading] = useState(false);
  const [isADLoading, setIsADLoading] = useState(false);
  const [isADGroupLoading, setIsADGroupLoading] = useState(false);
  const [isServiceLoading, setIsServiceLoading] = useState(false);
  const [isEndpointsLoading, setIsEndpointsLoading] = useState(false);
  const [isRadiusLoading, setIsRadiusLoading] = useState(false);
  const [isDatabaseLoading, setIsDatabaseLoading] = useState(false);

  const tenantDetails = useMemo(() => {
    return JSON.parse(localStorage.getItem("tenantDetails"));
  }, [location]);

  const tenantName = tenantDetails?.tenantName || tenantDetails?.domainName || "your tenant";

  useEffect(() => {
    if (!isEmpty(tenantDetails)) {
      const policyMode = tenantDetails.authenticationpolicy;
      setTenantData(
        policyMode === 1 ? "Audit" : tenantDetails.authenticationpolicy === 2 ? "Policy" : "Live"
      );
    }
  }, [tenantDetails]);

  useEffect(() => {
    fetchEndponitCount();
    fetchConnectionCount();
    fetchADCount();
    fetchADGroupCount();
    fetchServicePolicyCount();
    fetchEndpointsPolicyCount();
    fetchRadiusPolicyCount();
    fetchDatabasePolicyCount();
  }, [initialDayVal]);

  const fetchConnectionCount = () => {
    setIsConnectionLoading(true);
    let payload = {
      pageId: 1,
      pageSize: 500,
      search: "",
      filter: { filterBy: "", value: "" },
      endpoints: [],
      token: AUTH_TOKEN(),
      domainId: DOMAIN_ID(),
      orgId: ORG_ID(),
      date: moment().format("MM-DD-YYYY"),
    };
    getListConnections({ payload, onSuccess: handleConnectionSuccess, onFailure: handleConnectionFailure });
  };

  const handleConnectionSuccess = (data) => {
    setConnectionCount(data?.jumpServerConnections?.length || 0);
    setIsConnectionLoading(false);
  };

  const handleConnectionFailure = useCallback(() => {
    setIsConnectionLoading(false);
  }, []);

  const fetchEndponitCount = () => {
    setIsEndpointLoading(true);
    const endpointRequest = {
      orgId: ORG_ID(),
      domainId: DOMAIN_ID(),
      period: initialDayVal === "day" ? 7 : initialDayVal === "month" ? 30 : 1,
    };
    didAxios
      .post(`/dashboardService/listEndPointCount`, endpointRequest, {
        headers: { withCredentials: true, "X-Authorization": AUTH_TOKEN() },
      })
      .then(({ data }) => {
        setEndpointCount(data?.count);
      })
      .catch((error) => {
        manageServiceError(error, false, "Endpoint Count Failed");
      })
      .finally(() => setIsEndpointLoading(false));
  };

  const policyPayload = (filterValue) => ({
    orgId: ORG_ID(),
    tenantId: DOMAIN_ID(),
    requestId: "",
    page: 1,
    limit: 10,
    search: "",
    filters: [
      { filterParameter: "policyType", filterValue },
      { filterParameter: "iamUser", filterValue: getUserName() },
    ],
  });

  const fetchADCount = () => {
    setIsADLoading(true);
    appAxios
      .post(`/api/v1/policyService/ListPolicy`, policyPayload("AD"), {
        headers: { withCredentials: true, "X-Authorization": AUTH_TOKEN() },
      })
      .then(({ data }) => setADPolicyCount(data?.total_pages))
      .catch((err) => console.error("fetchADCount failed:", err?.response?.status, err?.message))
      .finally(() => setIsADLoading(false));
  };

  const fetchADGroupCount = () => {
    setIsADGroupLoading(true);
    appAxios
      .post(`/api/v1/policyService/ListPolicy`, policyPayload("GroupAD"), {
        headers: { withCredentials: true, "X-Authorization": AUTH_TOKEN() },
      })
      .then(({ data }) => setADGroupPolicyCount(data?.total_pages))
      .catch((err) => console.error("fetchADGroupCount failed:", err?.response?.status, err?.message))
      .finally(() => setIsADGroupLoading(false));
  };

  const fetchServicePolicyCount = () => {
    setIsServiceLoading(true);
    appAxios
      .post(`/api/v1/policyService/ListPolicy`, policyPayload("ServiceAccount"), {
        headers: { withCredentials: true, "X-Authorization": AUTH_TOKEN() },
      })
      .then(({ data }) => setServicePolicyCount(data?.total_pages))
      .catch((err) => console.error("fetchServicePolicyCount failed:", err?.response?.status, err?.message))
      .finally(() => setIsServiceLoading(false));
  };

  const fetchEndpointsPolicyCount = () => {
    setIsEndpointsLoading(true);
    appAxios
      .post(`/api/v1/policyService/ListPolicy`, policyPayload("endpoints"), {
        headers: { withCredentials: true, "X-Authorization": AUTH_TOKEN() },
      })
      .then(({ data }) => setEndpointsPolicyCount(data?.total_pages))
      .catch((err) => console.error("fetchEndpointsPolicyCount failed:", err?.response?.status, err?.message))
      .finally(() => setIsEndpointsLoading(false));
  };

  const fetchRadiusPolicyCount = () => {
    setIsRadiusLoading(true);
    appAxios
      .post(`/api/v1/policyService/ListPolicy`, policyPayload("radius"), {
        headers: { withCredentials: true, "X-Authorization": AUTH_TOKEN() },
      })
      .then(({ data }) => setRadiusPolicyCount(data?.total_pages))
      .catch((err) => console.error("fetchRadiusPolicyCount failed:", err?.response?.status, err?.message))
      .finally(() => setIsRadiusLoading(false));
  };

  const fetchDatabasePolicyCount = () => {
    setIsDatabaseLoading(true);
    appAxios
      .post(`/api/v1/policyService/ListPolicy`, policyPayload("database"), {
        headers: { withCredentials: true, "X-Authorization": AUTH_TOKEN() },
      })
      .then(({ data }) => setDatabasePolicyCount(data?.total_pages))
      .catch((err) => console.error("fetchDatabasePolicyCount failed:", err?.response?.status, err?.message))
      .finally(() => setIsDatabaseLoading(false));
  };

  const goPolicy = (policyType) =>
    navigate("/tenant/users/policies", { replace: true, state: { policyType } });

  const policyRows = [
    { tone: "doc",   icon: <FileText size={14} />, title: "AD Policy",              unit: "AD",              count: adPolicyCount,        loading: isADLoading,        onClick: () => goPolicy("AD") },
    { tone: "group", icon: <UsersIcon size={14} />, title: "AD Group Policy",        unit: "AD Group",        count: adGroupPolicyCount,   loading: isADGroupLoading,   onClick: () => goPolicy("GroupAD") },
    { tone: "svc",   icon: <Server size={14} />,    title: "Service Account Policy", unit: "Service Account", count: servicePolicyCount,   loading: isServiceLoading,   onClick: () => goPolicy("ServiceAccount") },
    { tone: "ep",    icon: <Globe size={14} />,     title: "Endpoints Policy",       unit: "Endpoints",       count: endpointsPolicyCount, loading: isEndpointsLoading, onClick: () => goPolicy("endpoints") },
    { tone: "rad",   icon: <Radio size={14} />,     title: "Radius Policy",          unit: "Radius",          count: radiusPolicyCount,    loading: isRadiusLoading,    onClick: () => goPolicy("radius") },
    { tone: "db",    icon: <Database size={14} />,  title: "Database Policy",        unit: "Database",        count: databasePolicyCount,  loading: isDatabaseLoading,  onClick: () => goPolicy("database") },
  ];

  const totalPolicies = policyRows.reduce((sum, r) => sum + (r.count || 0), 0);
  const computedAt = moment.utc().format("HH:mm");

  return (
    <>
      {contextHolder}
      <AppContentLayout
        children={
          <>
            <style>{`
              .ed-date-select.ant-select .ant-select-selector {
                border-radius: 7px !important;
                border: 1px solid ${AN_COLORS.line} !important;
                background: var(--an-surface) !important;
                height: 34px !important;
                padding: 0 12px !important;
                box-shadow: none !important;
                font-family: ${AN_FONT};
                font-size: 13px !important;
                color: ${AN_COLORS.ink2} !important;
                display: flex;
                align-items: center;
              }
              .ed-date-select .ant-select-selection-item {
                line-height: 32px !important;
              }
              .ed-date-select.ant-select-focused .ant-select-selector,
              .ed-date-select:hover .ant-select-selector {
                border-color: ${AN_COLORS.ink3} !important;
              }
            `}</style>
            <div
              style={{
                background: AN_COLORS.bgWarm,
                fontFamily: AN_FONT,
                color: AN_COLORS.ink,
                padding: "36px 56px 28px",
                minHeight: "100%",
              }}
            >
              {/* Masthead */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.3fr 1fr",
                  gap: 48,
                  alignItems: "end",
                  paddingBottom: 24,
                  borderBottom: `1px solid ${AN_COLORS.line}`,
                  marginBottom: 28,
                }}
              >
                <div>
                  <div
                    style={{
                      fontFamily: AN_MONO,
                      fontSize: 11,
                      letterSpacing: 1.6,
                      textTransform: "uppercase",
                      color: AN_COLORS.muted,
                      marginBottom: 10,
                    }}
                  >
                    Tenant · {tenantName} · {tenantData || "—"} mode
                  </div>
                  <h1
                    style={{
                      margin: 0,
                      fontSize: 44,
                      fontWeight: 500,
                      letterSpacing: -1.2,
                      lineHeight: 1.05,
                    }}
                  >
                    Your workspace,
                    <br />
                    <span style={{ color: AN_COLORS.muted }}>at a glance.</span>
                  </h1>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 18,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  <EdStat
                    label="Status"
                    value={
                      <>
                        <StatusDot status={tenantData} /> {tenantData || "—"}
                      </>
                    }
                  />
                  <EdStat label="Connections" value={connectionCount ?? 0} loading={isConnectionLoading} />
                  <EdStat label="Endpoints" value={endpointCount ?? 0} loading={isEndpointLoading} />
                </div>
              </div>

              {/* Section header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  marginBottom: 18,
                  gap: 16,
                }}
              >
                <div>
                  <div
                    style={{
                      fontFamily: AN_MONO,
                      fontSize: 11,
                      letterSpacing: 1.4,
                      textTransform: "uppercase",
                      color: AN_COLORS.muted,
                      marginBottom: 6,
                    }}
                  >
                    Section 01
                  </div>
                  <h2 style={{ margin: 0, fontSize: 24, fontWeight: 500, letterSpacing: -0.6 }}>
                    Policy Overview
                  </h2>
                  <p style={{ margin: "6px 0 0", fontSize: 13.5, color: AN_COLORS.muted, maxWidth: 520 }}>
                    Policies that shape what you can do in {tenantName}. Click any row to inspect.
                  </p>
                </div>
                <SimpleSelect
                  customClassName="ed-date-select w-36"
                  suffixIcon={<ChevronDown size={14} />}
                  allowClear={false}
                  defaultValue={initialDayVal}
                  value={initialDayVal}
                  handleOnchange={(val) => setInitialDayVal(val)}
                  selectOptions={[
                    { value: "hour", label: "Last Hour" },
                    { value: "day", label: "Last 7 Days" },
                    { value: "month", label: "Last 30 Days" },
                  ]}
                />
              </div>

              {/* Editorial rows */}
              <div
                style={{
                  background: "var(--an-surface)",
                  border: `1px solid ${AN_COLORS.line}`,
                  borderRadius: 12,
                  overflow: "hidden",
                }}
              >
                {policyRows.map((row, i) => (
                  <EditorialRow
                    key={row.title}
                    tone={row.tone}
                    icon={row.icon}
                    title={row.title}
                    count={row.count}
                    unit={row.unit}
                    loading={row.loading}
                    onClick={row.onClick}
                    first={i === 0}
                  />
                ))}
              </div>

              <div
                style={{
                  marginTop: 24,
                  fontSize: 12,
                  color: AN_COLORS.muted,
                  fontFamily: AN_MONO,
                }}
              >
                ↳ {totalPolicies} policies total · computed at {computedAt} UTC · live
              </div>
            </div>
          </>
        }
      />
    </>
  );
}

export default UserDashboard;
